import { Dialog, DialogContent } from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { MemberDto, MemberService } from '@services/api'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { MemberRoleEnumLabels } from '@utils/constants'
import { FormHeader } from '../forms/FormHeader'
import { UpTransition } from '../animations'
import { invalidateQcTestResult } from '@state/reducers/operationQualityControlsReducer'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import InputField from '../inputs/InputField'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { invalidateQcResultSchema } from '@utils/schemas'
import { InvalidateQcTestResultDto } from '@services/dtos'

interface Props {
  open: boolean
  onClose: () => void
  resultId: string
  controlLabel: string
  resultLabel: string
  entityType: 'preparationLot' | 'generatorElution'
  entityId: string
}

export function InvalidateQcResultDialog({ open, onClose, resultId, controlLabel, resultLabel, entityType, entityId }: Props) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const submitting = useAppSelector(({ operationQualityControls }) => operationQualityControls.submitting)
  const member = user && 'role' in user ? user as unknown as MemberDto : null

  const { control, handleSubmit, reset, setValue, formState: { errors }, formState: { isValid } } = useForm<InvalidateQcTestResultDto>({
    defaultValues: { comment: '', invalidatedByMemberId: member?.id },
    mode: 'onChange',
    resolver: yupResolver(invalidateQcResultSchema),
  })

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const getMemberLabel = (member: MemberDto | null) => {
    if (!member) {
      return ''
    }
    const roleLabel = MemberRoleEnumLabels[member.role]
    return `${member.firstName} ${member.lastName}${roleLabel ? ` (${roleLabel})` : ''}`
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async (data: InvalidateQcTestResultDto) => {
    try {
      await dispatch(invalidateQcTestResult({ resultId, dto: data, entityType, entityId })).unwrap()
      handleClose()
    } catch {
      dispatch(enqueueSnackbar({ message: 'Une erreur est survenue', options: { variant: 'error' } }))
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={UpTransition}>
      <DialogContent className="p-0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 p-6">
            <FormHeader
              title="Invalider le résultat"
              subTitles={[controlLabel, resultLabel]}
              onClose={handleClose}
              isSubmitting={submitting}
              submitText="Invalider"
              canSubmit={isValid}
            />
            <InputField
              control={control}
              name="comment"
              label="Commentaire"
              multiline
              minRows={2}
              size="small"
              autoFocus
            />
            <ServerAutocomplete<MemberDto>
              width="100%"
              defaultValue={member}
              initialOptions={member ? [member] : []}
              value={member ?? null}
              fetchService={fetchMembers}
              onChange={(value: MemberDto | null) => {
                if (value) {
                  setValue('invalidatedByMemberId', value.id)
                }
              }}
              getValue={(value) => value?.id?.toString() || null}
              getOptionLabel={(value) => getMemberLabel(value as MemberDto | null)}
              debounceMs={300}
              placeholder="Rechercher un membre..."
              label="Agent"
              helperText={errors.invalidatedByMemberId?.message}
              error={!!errors.invalidatedByMemberId}
              minSearchLength={0}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
